import { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Download, History, RotateCcw } from 'lucide-react';
import { getSDK } from '@/store/sdkStore';
import { friendlyError } from '@/lib/errors';
import { fileBase64 } from '@/lib/fileBytes';
import { downloadBytes } from '@/lib/printForm';
import { confirmDialog } from '@/lib/dialogs';
import { useToastStore } from '@/store/toastStore';

// ============================================================================
// M4 «Версии файлов» — история версий в предпросмотре файла (SSoT — сервер):
// список (v, дата, размер, комментарий), скачивание версии, восстановление
// (создаёт НОВУЮ версию — история неизменяема).
// ============================================================================

interface VersionItem {
  id: string;
  version: number;
  size: number;
  comment: string;
  createdAt: string;
}

export function FileVersions({ fileId, fileName, onRestored }: {
  fileId: string;
  fileName: string;
  onRestored: () => void;
}) {
  const [items, setItems] = useState<VersionItem[] | null>(null);
  const [current, setCurrent] = useState(0);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r: any = await getSDK().files.listFileVersions({ fileId });
    setItems((r?.items || []).map((v: any) => ({
      id: String(v.id || ''),
      version: Number(v.version || 0),
      size: Number(v.size || 0),
      comment: String(v.comment || ''),
      createdAt: String(v.createdAt || ''),
    })));
    setCurrent(Number(r?.currentVersion || 0));
  };

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const r: any = await getSDK().files.listFileVersions({ fileId });
        if (!alive) return;
        setItems((r?.items || []).map((v: any) => ({
          id: String(v.id || ''),
          version: Number(v.version || 0),
          size: Number(v.size || 0),
          comment: String(v.comment || ''),
          createdAt: String(v.createdAt || ''),
        })));
        setCurrent(Number(r?.currentVersion || 0));
      } catch (e) {
        if (!alive) return;
        useToastStore.getState().showError(friendlyError(e));
        setItems([]);
      }
    })();
    return () => { alive = false; };
  }, [fileId]);

  const downloadVersion = async (version: number) => {
    setBusy(true);
    try {
      const r: any = await getSDK().files.downloadFileVersion({ fileId, version });
      const dot = fileName.lastIndexOf('.');
      const name = dot > 0 ? `${fileName.slice(0, dot)} (v${version})${fileName.slice(dot)}` : `${fileName} (v${version})`;
      downloadBytes(name, String(r?.fileType || 'application/octet-stream'), fileBase64(r?.data));
    } catch (e) {
      useToastStore.getState().showError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const restoreVersion = async (version: number) => {
    if (!await confirmDialog(`Восстановить содержимое из версии v${version}? Будет создана НОВАЯ версия (история сохранится).`)) return;
    setBusy(true);
    try {
      await getSDK().files.restoreFileVersion({ fileId, version });
      await load();
      onRestored();
      useToastStore.getState().showSuccess(`Восстановлено из v${version}`);
    } catch (e) {
      useToastStore.getState().showError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  if (items === null || items.length === 0) return null;

  return (
    <div className="mb-2 rounded border bg-muted/20">
      <button
        type="button"
        className="flex w-full items-center gap-1.5 px-2 py-1.5 text-[11px] text-muted-foreground hover:bg-accent/5"
        onClick={() => setOpen((o) => !o)}
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        <History className="h-3 w-3" />
        Версии ({items.length})
        <span className="ml-auto">текущая: v{current}</span>
      </button>
      {open && (
        <div className="space-y-1 px-2 pb-2">
          {items.map((v) => (
            <div key={v.id} className="flex items-center gap-2 text-[11px]">
              <span className={`w-8 font-medium ${v.version === current ? 'text-primary' : ''}`}>v{v.version}</span>
              <span className="text-muted-foreground">{v.createdAt ? new Date(v.createdAt).toLocaleString('ru-RU') : ''}</span>
              <span className="text-muted-foreground">{fmtSize(v.size)}</span>
              {v.comment && <span className="max-w-[220px] truncate text-muted-foreground" title={v.comment}>{v.comment}</span>}
              <span className="ml-auto flex gap-1">
                <button
                  type="button"
                  className="rounded border px-1.5 py-0.5 hover:bg-accent disabled:opacity-50"
                  disabled={busy}
                  title="Скачать версию"
                  onClick={() => void downloadVersion(v.version)}
                >
                  <Download className="h-3 w-3" />
                </button>
                {v.version !== current && (
                  <button
                    type="button"
                    className="rounded border px-1.5 py-0.5 hover:bg-accent disabled:opacity-50"
                    disabled={busy}
                    title="Восстановить (новая версия)"
                    onClick={() => void restoreVersion(v.version)}
                  >
                    <RotateCcw className="h-3 w-3" />
                  </button>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function fmtSize(n: number): string {
  if (!n) return '0 Б';
  if (n < 1024) return `${n} Б`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} КБ`;
  return `${(n / 1024 / 1024).toFixed(1)} МБ`;
}
